import React, { useState, useRef } from "react";
import { connect } from "react-redux";
import './MailingImageUploader.css';

import { useTranslation } from 'react-i18next';
import PreviewFileDialog from "src/app/shared/components/preview-file-dialog/PreviewFileDialog";
import { FileType } from "src/app/shared/components/choose-file-dialog/ChooseFileDialog";
import { IMailingImageUploaderProps } from "./~types/MailingImageUploaderProps";
import { IApplicationState } from "src/~store/models/ApplicationState";
import { changeImageFile } from "src/app/mailing/~store/actions/actions";
import { mailingItemInfoSelector } from "src/app/mailing/~store/selector";
import { Label } from "reactstrap";
import { getSupportedImageTypes } from "src/app/shared/helpers/img-helpers/ImageSupportHelper";
import i18n from "src/app/shared/localization/i18n";
import { showErrorToastr } from "src/app/shared/helpers/toastr-helper/ToastrHelper";
import { FILE_MAX_SIZE, FILE_SIZE_WARNING } from "src/~store/constants";


const MailingImageUploader = (props: ReturnType<typeof mapStateToProps> & IMailingImageUploaderProps) => {
    const { t } = useTranslation();

    const inputRef = useRef<HTMLInputElement>(null);
    const [fileName, setFileName] = useState<string>();
    const [imageFile, setImageFile] = useState<File>();

    const currentImage = imageFile || props.mailingItemInfo.image;
    const isSizeWarning = currentImage ? (currentImage.size > FILE_SIZE_WARNING && currentImage.size < FILE_MAX_SIZE) : false;
    const displayFileName = fileName || props.mailingItemInfo.pictureId || props.mailingItemInfo.image?.name;

    const handleChange = (e: any) => {
        if(e.target.files.length === 0) return;
        const file = e.target.files[0] as File;

        if (file.size >= FILE_MAX_SIZE){
            showErrorToastr(i18n.t("error.fileSizeExceeded", {
                currentSizeMb: (file.size / (1024 * 1024)).toFixed(1),
                maxSizeMb: (FILE_MAX_SIZE / (1024 * 1024))
            }))
            e.target.value = '';
            return;
        }

        if (file !== undefined) {
            setImageFile(file);
        }

        setTimeout(() => {
            document.getElementById('mailing-item-info__image-block-input-hidden')?.removeAttribute('type');
            document.getElementById('mailing-item-info__image-block-input-hidden')?.removeAttribute('accept');
        }, 1000)
    }

    const buildFileSelector = () => {
        const fileSelector = inputRef.current;
        if(!fileSelector) return;
        fileSelector.setAttribute('type', 'file');
        fileSelector.setAttribute('accept', getSupportedImageTypes());
        fileSelector.onchange = handleChange;
        return fileSelector;
    }

    const _onClickInputFieldHandler = () => {
        buildFileSelector()?.click();
    }

    const _onDiscardImageButtonHandler = () => {
        setFileName(undefined);
        setImageFile(undefined);
        props.changeImageFile(undefined);
    }

    const _onImageFileUploaded = () => {
        setFileName(imageFile?.name);
        props.changeImageFile(imageFile);
    }


    return (
        <div className="mailing-item-info__image">
            <div className="mailing-item-info__image-block">
                <input
                    ref={inputRef}
                    className="mailing-item-info__image-block-input-hidden"
                    id="mailing-item-info__image-block-input-hidden"
                    type="file"
                />
                <div className="mailing-item-info__image-block-input-field">
                    <Label
                        className="mailing-item-info__image-block-input-field-label"
                        tabIndex={0}
                        onClick={_onClickInputFieldHandler}
                        onKeyUp={(e) => e.key === "Enter" && _onClickInputFieldHandler()}
                    >
                        {t('mailing.uploadImage')}
                    </Label>

                    {(displayFileName) && (
                        <div className="file-name-row">
                            <div className="uploaded-image__file-name">
                                {displayFileName}
                            </div>
                            <i
                                className="material-icons remove-image-button"
                                tabIndex={0}
                                onClick={_onDiscardImageButtonHandler}
                                onKeyUp={(e) => e.key === "Enter" && _onDiscardImageButtonHandler()}
                            >
                                clear
                            </i>
                            {(isSizeWarning) && ( <span className="warning-icon" title={t('mailing.fileMayNotBeDelivered')}>&#x26A0;</span>)}
                        </div>
                    )}
                </div>
                <PreviewFileDialog
                    file={imageFile}
                    type={FileType.Image}
                    onFileSend={_onImageFileUploaded}
                />
            </div>
        </div>
    )
};

const mapStateToProps = (state: IApplicationState) => {
    return {
        mailingItemInfo: mailingItemInfoSelector(state)
    };
};

const mapDispatchToProps = (dispatch: any): IMailingImageUploaderProps => {
    return {
        changeImageFile: (image: File | undefined) => dispatch(changeImageFile(image))
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(MailingImageUploader);